import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useField, useFormikContext } from 'formik';

import CaseStatusChip from 'components/v2/common/caseStatusChip';

import type { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { BILLABLE_CASE_STATUSES, CASE_CREATED_STATUS } from 'data/constants/serviceProviderBilling';
import { getClientStatusLabelKey } from 'helpers/applicationRetainers';
import useTranslate from 'hooks/useTranslate';

interface InstallmentStatusSelectProps {
    name: string;
    /** Statuses already collected by another installment of the same rule. */
    usedStatuses: ClientApplicationStatus[];
    disabled?: boolean;
}

const InstallmentStatusSelect = ({
    name,
    usedStatuses,
    disabled = false,
}: InstallmentStatusSelectProps) => {
    const { translate } = useTranslate();
    const [field, meta, helpers] = useField<ClientApplicationStatus[]>(name);
    const { submitCount } = useFormikContext();
    const isError = Boolean(meta.error && (meta.touched || submitCount > 0));

    return (
        <Autocomplete
            multiple
            fullWidth
            size="small"
            disableCloseOnSelect
            disabled={disabled}
            options={BILLABLE_CASE_STATUSES}
            value={field.value ?? []}
            onChange={(_event, value) => {
                void helpers.setValue(value);
            }}
            onBlur={() => {
                void helpers.setTouched(true);
            }}
            getOptionLabel={(status) => translate(getClientStatusLabelKey(status) ?? status)}
            getOptionDisabled={(status) => usedStatuses.includes(status)}
            renderOption={({ key, ...optionProps }, status) => (
                <li key={key} {...optionProps}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <CaseStatusChip status={status} />
                        {status === CASE_CREATED_STATUS ? (
                            <Typography variant="caption" color="text.secondary">
                                {translate('serviceProviderBilling.caseCreatedHint')}
                            </Typography>
                        ) : null}
                    </Stack>
                </li>
            )}
            renderValue={(value, getItemProps) =>
                value.map((status, index) => {
                    const { key, ...itemProps } = getItemProps({ index });
                    return <CaseStatusChip key={key} status={status} {...itemProps} />;
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={`${translate('serviceProviderBilling.schedule.statuses')} *`}
                    error={isError}
                    helperText={isError ? String(meta.error) : ' '}
                />
            )}
        />
    );
};

export default InstallmentStatusSelect;
